import { useReducer, useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { generateClient } from 'aws-amplify/api';
import { useAuth } from '../providers/AuthProvider';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { ErrorMessage } from '../components/ErrorMessage';
import { PotencySummary } from '../components/recipes/PotencySummary';
import { IngredientRow } from '../components/recipes/IngredientRow';
import { calculatePotency } from '../utils/potency';
import { validateRecipe, validateIngredient } from '../utils/validation';
import { getRecipe, createRecipe, updateRecipe } from '../graphql/recipes';
import type { RecipeIngredient, Recipe, InfusedIngredientInput, ValidationError } from '../types/recipe';

const client = generateClient();

const MAX_INGREDIENTS = 100;

// --- State and Reducer ---

interface RecipeFormState {
  title: string;
  description: string;
  servings: number;
  ingredients: RecipeIngredient[];
  instructions: string;
}

type RecipeFormAction =
  | { type: 'SET_FIELD'; field: keyof Omit<RecipeFormState, 'ingredients'>; value: string | number }
  | { type: 'SET_INGREDIENT'; index: number; ingredient: RecipeIngredient }
  | { type: 'ADD_INGREDIENT' }
  | { type: 'REMOVE_INGREDIENT'; index: number }
  | { type: 'LOAD_RECIPE'; recipe: Recipe };

function createEmptyIngredient(): RecipeIngredient {
  return {
    name: '',
    amount: 0,
    unit: '',
    isCannabisInfused: false,
  };
}

const initialState: RecipeFormState = {
  title: '',
  description: '',
  servings: 1,
  ingredients: [createEmptyIngredient()],
  instructions: '',
};

function recipeFormReducer(state: RecipeFormState, action: RecipeFormAction): RecipeFormState {
  switch (action.type) {
    case 'SET_FIELD':
      return { ...state, [action.field]: action.value };
    case 'SET_INGREDIENT':
      return {
        ...state,
        ingredients: state.ingredients.map((ing, i) =>
          i === action.index ? action.ingredient : ing
        ),
      };
    case 'ADD_INGREDIENT':
      if (state.ingredients.length >= MAX_INGREDIENTS) return state;
      return {
        ...state,
        ingredients: [...state.ingredients, createEmptyIngredient()],
      };
    case 'REMOVE_INGREDIENT':
      return {
        ...state,
        ingredients: state.ingredients.filter((_, i) => i !== action.index),
      };
    case 'LOAD_RECIPE':
      return {
        title: action.recipe.title,
        description: action.recipe.description || '',
        servings: action.recipe.servings,
        ingredients: action.recipe.ingredients,
        instructions: action.recipe.instructions?.join('\n') || '',
      };
    default:
      return state;
  }
}

// --- Component ---

export function RecipeBuilderPage() {
  const { recipeId } = useParams<{ recipeId?: string }>();
  const { user } = useAuth();
  const isEditMode = !!recipeId;

  const navigate = useNavigate();

  const [state, dispatch] = useReducer(recipeFormReducer, initialState);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<ValidationError[]>([]);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Fetch recipe data in edit mode
  useEffect(() => {
    if (!isEditMode || !user) return;

    async function fetchRecipe() {
      setIsLoading(true);
      setLoadError(null);
      try {
        const response = await client.graphql({
          query: getRecipe,
          variables: { userId: user!.userId, recipeId },
        });
        const recipe = (response as { data: { getRecipe: Recipe } }).data.getRecipe;
        if (!recipe) {
          setLoadError('Recipe not found.');
        } else {
          dispatch({ type: 'LOAD_RECIPE', recipe });
        }
      } catch {
        setLoadError('Failed to load recipe. Please try again.');
      } finally {
        setIsLoading(false);
      }
    }

    fetchRecipe();
  }, [isEditMode, recipeId, user]);

  // Live potency calculation
  const potencyResult = useMemo(() => {
    const infusedIngredients: InfusedIngredientInput[] = state.ingredients
      .filter((ing) => ing.isCannabisInfused)
      .map((ing) => ({
        name: ing.name || 'Unnamed',
        cannabinoidType: ing.cannabinoidType || 'THC',
        cannabinoidPercentage: ing.cannabinoidPercentage || 0,
        materialWeight: ing.materialWeight || 0,
        decarboxylationEfficiency: ing.decarboxylationEfficiency || 0,
        extractionEfficiency: ing.extractionEfficiency || 0,
      }));

    return calculatePotency(infusedIngredients, state.servings || 1);
  }, [state.ingredients, state.servings]);

  const hasInfusedIngredients = state.ingredients.some((ing) => ing.isCannabisInfused);

  // Handlers
  function handleIngredientChange(index: number, ingredient: RecipeIngredient) {
    dispatch({ type: 'SET_INGREDIENT', index, ingredient });
  }

  function handleIngredientRemove(index: number) {
    dispatch({ type: 'REMOVE_INGREDIENT', index });
  }

  function handleAddIngredient() {
    dispatch({ type: 'ADD_INGREDIENT' });
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitError(null);

    // Run validation
    const recipeErrors = validateRecipe({
      title: state.title,
      servings: state.servings,
      ingredients: state.ingredients,
    });

    const ingredientErrors = state.ingredients.flatMap((ingredient, index) =>
      validateIngredient(ingredient, index)
    );

    const allErrors = [...recipeErrors, ...ingredientErrors];
    setValidationErrors(allErrors);

    if (allErrors.length > 0) {
      return;
    }

    // Prepare ingredients for GraphQL — strip infused fields from non-infused ingredients
    const cleanedIngredients = state.ingredients.map((ing) => {
      const base = {
        name: ing.name,
        amount: ing.amount,
        unit: ing.unit,
        isCannabisInfused: ing.isCannabisInfused,
      };

      if (ing.isCannabisInfused) {
        return {
          ...base,
          cannabinoidPercentage: ing.cannabinoidPercentage,
          cannabinoidType: ing.cannabinoidType,
          materialWeight: ing.materialWeight,
          decarboxylationEfficiency: ing.decarboxylationEfficiency,
          extractionEfficiency: ing.extractionEfficiency,
        };
      }

      return base;
    });

    // Calculate potency for storage
    const infusedIngredients: InfusedIngredientInput[] = state.ingredients
      .filter((ing) => ing.isCannabisInfused)
      .map((ing) => ({
        name: ing.name || 'Unnamed',
        cannabinoidType: ing.cannabinoidType || 'THC',
        cannabinoidPercentage: ing.cannabinoidPercentage || 0,
        materialWeight: ing.materialWeight || 0,
        decarboxylationEfficiency: ing.decarboxylationEfficiency || 0,
        extractionEfficiency: ing.extractionEfficiency || 0,
      }));
    const potency = calculatePotency(infusedIngredients, state.servings || 1);

    // Prepare instructions as array (split by newlines, filter empty lines)
    const instructions = state.instructions
      .split('\n')
      .filter((line) => line.trim().length > 0);

    setIsSubmitting(true);

    try {
      if (isEditMode) {
        // Edit mode: send updateRecipe mutation
        await client.graphql({
          query: updateRecipe,
          variables: {
            input: {
              userId: user!.userId,
              recipeId: recipeId!,
              title: state.title,
              description: state.description || undefined,
              servings: state.servings,
              ingredients: cleanedIngredients,
              instructions: instructions.length > 0 ? instructions : undefined,
              totalCannabinoidMg: potency.totalMg,
              perServingMg: potency.perServingMg,
              updatedAt: new Date().toISOString(),
            },
          },
        });
        navigate(`/recipes/${recipeId}`);
      } else {
        // Create mode: generate UUID, send createRecipe mutation
        const newRecipeId = crypto.randomUUID();
        const now = new Date().toISOString();

        await client.graphql({
          query: createRecipe,
          variables: {
            input: {
              userId: user!.userId,
              recipeId: newRecipeId,
              title: state.title,
              description: state.description || undefined,
              servings: state.servings,
              ingredients: cleanedIngredients,
              instructions: instructions.length > 0 ? instructions : undefined,
              totalCannabinoidMg: potency.totalMg,
              perServingMg: potency.perServingMg,
              createdAt: now,
              updatedAt: now,
            },
          },
        });
        navigate('/recipes');
      }
    } catch {
      setSubmitError(
        isEditMode
          ? 'Failed to update recipe. Please try again.'
          : 'Failed to save recipe. Please try again.'
      );
    } finally {
      setIsSubmitting(false);
    }
  }

  // Render
  if (isLoading) {
    return (
      <div className="page recipe-builder-page">
        <LoadingSpinner />
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="page recipe-builder-page">
        <ErrorMessage message={loadError} />
      </div>
    );
  }

  return (
    <div className="page recipe-builder-page">
      <h2>{isEditMode ? 'Edit Recipe' : 'Create Recipe'}</h2>

      <form onSubmit={handleSubmit} noValidate>
        <div className="form-group">
          <label htmlFor="recipe-title">Title</label>
          <input
            id="recipe-title"
            type="text"
            value={state.title}
            onChange={(e) => dispatch({ type: 'SET_FIELD', field: 'title', value: e.target.value })}
            maxLength={150}
            aria-describedby={
              validationErrors.find((e) => e.field === 'title')
                ? 'recipe-title-error'
                : undefined
            }
            aria-invalid={!!validationErrors.find((e) => e.field === 'title')}
          />
          {validationErrors.find((e) => e.field === 'title') && (
            <span id="recipe-title-error" className="field-error" role="alert">
              {validationErrors.find((e) => e.field === 'title')!.message}
            </span>
          )}
        </div>

        <div className="form-group">
          <label htmlFor="recipe-description">Description</label>
          <textarea
            id="recipe-description"
            value={state.description}
            onChange={(e) =>
              dispatch({ type: 'SET_FIELD', field: 'description', value: e.target.value })
            }
            maxLength={500}
            rows={3}
          />
        </div>

        <div className="form-group">
          <label htmlFor="recipe-servings">Servings</label>
          <input
            id="recipe-servings"
            type="number"
            value={state.servings}
            onChange={(e) =>
              dispatch({
                type: 'SET_FIELD',
                field: 'servings',
                value: e.target.value === '' ? 1 : parseInt(e.target.value, 10),
              })
            }
            min={1}
            max={999}
            step={1}
            aria-describedby={
              validationErrors.find((e) => e.field === 'servings')
                ? 'recipe-servings-error'
                : undefined
            }
            aria-invalid={!!validationErrors.find((e) => e.field === 'servings')}
          />
          {validationErrors.find((e) => e.field === 'servings') && (
            <span id="recipe-servings-error" className="field-error" role="alert">
              {validationErrors.find((e) => e.field === 'servings')!.message}
            </span>
          )}
        </div>

        <fieldset className="ingredients-section">
          <legend>Ingredients</legend>
          {validationErrors.find((e) => e.field === 'ingredients') && (
            <span className="field-error" role="alert">
              {validationErrors.find((e) => e.field === 'ingredients')!.message}
            </span>
          )}
          {state.ingredients.map((ingredient, index) => (
            <IngredientRow
              key={index}
              ingredient={ingredient}
              index={index}
              onChange={handleIngredientChange}
              onRemove={handleIngredientRemove}
              errors={validationErrors}
            />
          ))}
          <button
            type="button"
            className="cta-button"
            onClick={handleAddIngredient}
            disabled={state.ingredients.length >= MAX_INGREDIENTS}
          >
            Add Ingredient
          </button>
          {state.ingredients.length >= MAX_INGREDIENTS && (
            <p className="field-hint">Maximum of {MAX_INGREDIENTS} ingredients reached.</p>
          )}
        </fieldset>

        <div className="form-group">
          <label htmlFor="recipe-instructions">Instructions</label>
          <textarea
            id="recipe-instructions"
            value={state.instructions}
            onChange={(e) =>
              dispatch({ type: 'SET_FIELD', field: 'instructions', value: e.target.value })
            }
            maxLength={5000}
            rows={8}
          />
        </div>

        {hasInfusedIngredients && (
          <PotencySummary potencyResult={potencyResult} servings={state.servings || 1} />
        )}

        {submitError && (
          <ErrorMessage message={submitError} />
        )}

        <button
          type="submit"
          className="cta-button"
          disabled={isSubmitting}
        >
          {isSubmitting
            ? 'Saving...'
            : isEditMode
              ? 'Save Changes'
              : 'Create Recipe'}
        </button>
      </form>
    </div>
  );
}
